/**
 * SettingsDialog — shared style objects.
 *
 * Extracted from SettingsDialog.tsx to keep the component file focused on
 * the section components and logic. Pure CSSProperties presets built on the
 * shared theme tokens.
 */

import React from 'react';
import { color, font, ui, z } from '../ui/theme';

// ── Styles ───────────────────────────────────────────────────────────────────

// Unified glass-modal shell (shared ui.dialog) + this dialog's fixed-window bits.
export const windowStyle: React.CSSProperties = {
  ...ui.dialog,
  position: 'fixed',
  zIndex: z.modal,
  userSelect: 'none',
};

export const titleBarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 12px 0 16px',
  height: 44,
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.14)',
  cursor: 'grab',
  flexShrink: 0,
};

// Uppercase title — unified white text across the settings.
export const titleStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: color.ink,
  fontFamily: font.sans,
  display: 'flex', alignItems: 'center', gap: 8,
  pointerEvents: 'none',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
};

export const closeBtnStyle: React.CSSProperties = { ...ui.closeBtn };

export const bodyStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  minHeight: 0,
};

export const tabListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  padding: '10px 8px',
  borderRight: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.1)',
  minWidth: 140,
  flexShrink: 0,
  overflowY: 'auto',
  background: 'rgba(8, 6, 18, 0.4)',
};

export const tabBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  border: '1px solid transparent',
  borderRadius: 999,
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
  textAlign: 'left',
  background: 'transparent',
};

export const contentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '14px 18px',
  minWidth: 0,
};

export const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

export const sectionLabel: React.CSSProperties = {
  fontSize: 10,
  color: color.ink,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1.2,
  marginBottom: 6,
  fontFamily: font.sans,
};

export const subSectionLabel: React.CSSProperties = {
  fontSize: 9,
  color: color.ink,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 1,
  marginTop: 8,
  marginBottom: 4,
  fontFamily: font.sans,
};

export const resetBtnStyle: React.CSSProperties = {
  background: 'rgba(248, 113, 113, 0.08)',
  border: '1px solid rgba(248, 113, 113, 0.25)',
  color: '#F87171',
  borderRadius: 999,
  padding: '9px 12px',
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 600,
  fontFamily: font.sans,
  width: '100%',
  marginTop: 14,
  transition: 'all 0.2s ease',
};

export const hintTextStyle: React.CSSProperties = {
  fontSize: 9,
  color: color.ink,
  lineHeight: 1.5,
  marginBottom: 8,
  fontFamily: font.sans,
};

export const accordionBtnStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  // Match Flow's inactive button/surface tokens + fully round (pill) ends.
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 999,
  padding: '9px 16px',
  cursor: 'pointer',
  color: color.ink,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
};

export const applyBtnStyle: React.CSSProperties = {
  flex: 1,
  background: 'linear-gradient(135deg, oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2), oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.12))',
  border: '1px solid oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.35)',
  color: color.accent,
  borderRadius: 999,
  padding: '9px 12px',
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 600,
  fontFamily: font.sans,
  transition: 'all 0.2s ease',
};

export const resizeHandleStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: 20,
  height: 20,
  cursor: 'nwse-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const colorRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
};

export const colorLabelStyle: React.CSSProperties = {
  fontSize: 10, color: color.inkDim, width: 90,
  fontFamily: font.sans,
};

export const colorInputStyle: React.CSSProperties = {
  width: 28, height: 22, border: '1px solid oklch(0.78 calc(0.215 * var(--accent-c)) var(--accent-h) / 0.2)',
  borderRadius: 5, cursor: 'pointer', background: 'transparent', padding: 0,
};

export const colorHexStyle: React.CSSProperties = {
  fontSize: 9, color: color.inkFaint,
  fontFamily: font.sans,
  width: 60,
};

export const toggleRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2,
};

export const toggleTrackStyle: React.CSSProperties = {
  width: 32, height: 18,
  borderRadius: 9,
  border: '1px solid',
  cursor: 'pointer',
  position: 'relative',
  padding: 2,
  transition: 'all 0.2s ease',
  flexShrink: 0,
};

export const toggleThumbStyle: React.CSSProperties = {
  width: 12, height: 12,
  borderRadius: '50%',
  background: color.ink,
  transition: 'transform 0.2s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
};

export const fieldLabelStyle: React.CSSProperties = {
  fontSize: 10, color: color.inkDim, minWidth: 80,
  fontFamily: font.sans,
};

export const selectStyle: React.CSSProperties = {
  ...ui.input,
  padding: '4px 8px', fontSize: 10, flex: 1,
};

export const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 10,
  color: color.ink,
  fontFamily: font.sans,
  cursor: 'pointer',
  userSelect: 'none',
};

export const checkboxStyle: React.CSSProperties = {
  cursor: 'pointer',
  accentColor: color.accent,
};

// ── Keybinding styles ─────────────────────────────────────────────────────

export const kbHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '5px 8px',
  background: 'oklch(0.30 calc(0.05 * var(--tint-c)) var(--tint-h) / 0.3)',
  borderRadius: 6,
  marginBottom: 4,
};

export const kbRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '6px 8px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.06)',
};

export const kbKeyBtn: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
  color: color.ink,
  fontSize: 10,
  fontFamily: font.sans,
  cursor: 'pointer',
  minWidth: 70,
  textAlign: 'center' as const,
  transition: 'all 0.15s ease',
};
